'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import Hls from 'hls.js';

/**
 * useMuxHls — attach a signed Mux HLS stream to an existing `<video>` element
 * (BAL-721 / R3).
 *
 * Vendor-neutral: plays the standard `stream.mux.com/…m3u8` HLS URL via hls.js
 * (Chrome/Firefox, which play neither HLS nor the raw .mov natively) or the
 * browser's native HLS (Safari). The Mux SDK is never imported — the signed,
 * short-lived token (≤15 min, Rule 11) is fetched at play time from
 * `/api/media/mux-token`, which mints it behind the VideoService boundary.
 *
 * Attaching to a caller-owned `<video>` (rather than rendering one) lets rich
 * players (the auto-play/mute/intersection reel) keep all their existing
 * behaviour — they just get an HLS source instead of a native `src`.
 *
 * Lives in `@kit/uploads` (relocated from apps/web) so the media-picker cluster
 * — a shared package that cannot import app code — can play Mux the same way the
 * app surfaces do. It only depends on hls.js + a same-origin fetch to the app's
 * `/api/media/mux-token` route, so it is fully portable.
 *
 * @param videoRef ref to the target `<video>`.
 * @param mediaId  artist_media id to stream, or `null` to disable (native mode).
 */
export function useMuxHls(
  videoRef: RefObject<HTMLVideoElement | null>,
  mediaId: string | null | undefined,
  options?: { autoPlay?: boolean; onError?: () => void },
): { failed: boolean } {
  // Deliberately per-MOUNT, not per-mediaId. Resetting this on a mediaId change
  // cannot work from inside the hook: callers unmount the <video> while
  // `failed` is true, so on the next attempt `videoRef.current` is already null
  // and the effect fails again before any success could clear the flag —
  // measured, a clip that recovers stays dead. Recovery therefore belongs to
  // the caller, which must REMOUNT on a media change so this starts false with
  // the element present. Both callers key on the media: MuxHlsPlayer below,
  // and DancerReel via `key={reel.id}` at its mount site.
  const [failed, setFailed] = useState(false);
  const autoPlay = options?.autoPlay ?? false;

  // Hold onError in a ref so an inline `onError={() => …}` from a caller does
  // not re-fire the token-fetch/hls effect (which depends only on the stable
  // inputs below) on every render. Updated in a passive effect — refs must not
  // be written during render.
  const onErrorRef = useRef(options?.onError);
  useEffect(() => {
    onErrorRef.current = options?.onError;
  });

  useEffect(() => {
    if (!mediaId) return;

    let hls: Hls | null = null;
    let cancelled = false;
    // The <video> the native-HLS branch attached its error listener to, so the
    // cleanup can detach it (the element outlives this effect across renders).
    let nativeErrorTarget: HTMLVideoElement | null = null;

    const fail = () => {
      if (cancelled) return;
      // Release the MSE attachment here, not only in cleanup. A caller that
      // swaps the <video> out on failure (DancerReel renders an "unavailable"
      // card instead) leaves this effect's deps untouched, so cleanup never
      // runs and the instance would stay bound to a detached element for the
      // life of the page.
      // `hls = null` before the destroy: cleanup must not destroy it a second
      // time if the component unmounts after a fatal error.
      const dying = hls;
      hls = null;
      if (dying) {
        // Deferred a tick, and belt-and-braces rather than a necessity: `fail`
        // can be called from inside hls.js's own ERROR dispatch, but 1.6.16 is
        // measured tolerant of a synchronous destroy there — real library,
        // real Chromium, a manifest that hard-fails, zero page errors either
        // way (2026-08-28). Kept because it costs nothing and re-entrancy is
        // exactly the kind of thing a future version can regress. Guarded
        // because this runs detached from any caller — an uncaught throw would
        // surface as a page error and Sentry noise for a teardown with no
        // user-visible consequence.
        queueMicrotask(() => {
          try {
            dying.destroy();
          } catch {
            // Already torn down, or torn down mid-flight. Nothing to do.
          }
        });
      }
      setFailed(true);
      onErrorRef.current?.();
    };

    /** Native-branch `error` handler — fatal-only, see the branch comment. */
    const onNativeError = () => {
      const el = nativeErrorTarget;
      // readyState 0 = nothing decoded, ever. MEDIA_ERR_SRC_NOT_SUPPORTED (4)
      // = the browser rejected the source outright. Anything else (a network
      // blip on a segment, a decode error after playback started) is transient
      // and must not tear down a player the viewer is already watching.
      if (!el || el.readyState === 0 || el.error?.code === 4) fail();
    };

    void (async () => {
      try {
        const res = await fetch(
          `/api/media/mux-token?id=${encodeURIComponent(mediaId)}`,
        );
        if (!res.ok) {
          fail();
          return;
        }
        const data = (await res.json()) as {
          playbackId?: string;
          playbackToken?: string;
          thumbnailToken?: string;
        };
        if (cancelled) return;

        const video = videoRef.current;
        if (!video || !data.playbackId || !data.playbackToken) {
          fail();
          return;
        }

        const src = `https://stream.mux.com/${data.playbackId}.m3u8?token=${data.playbackToken}`;

        // Poster/still from Mux (Phase 4) — the signed thumbnail URL.
        if (data.thumbnailToken) {
          video.poster = `https://image.mux.com/${data.playbackId}/thumbnail.jpg?token=${data.thumbnailToken}`;
        }

        // Pick the playback path. `canPlayType('application/vnd.apple.mpegurl')`
        // alone is NOT a test for native HLS and choosing on it was the bug this
        // file carried until 2026-08-28: Chrome/macOS answers `"maybe"` to it —
        // truthy — while being unable to demux HLS at all. Every Chrome viewer
        // therefore took the native branch, got the raw .m3u8 assigned to
        // `video.src`, and sat at readyState 0 / networkState 3 with
        // MEDIA_ERR_SRC_NOT_SUPPORTED. Nothing threw, so `fail()` never ran and
        // no caller ever showed its retry surface: an inert player whose play
        // button did nothing, on every Mux video in the product.
        //
        // Measured 2026-08-28, same probe on both engines:
        //   Chrome 152/macOS  canPlayType="maybe"  ManagedMediaSource=undefined
        //   WebKit 26.4       canPlayType="maybe"  ManagedMediaSource=function
        //
        // `ManagedMediaSource` is the discriminator, and it is a feature test,
        // not UA sniffing: it ships only in WebKit (Safari 17.1+ / iOS 17.1+),
        // and every engine that has it also has genuine native HLS. Older
        // WebKit has neither it nor MSE, so `!Hls.isSupported()` catches that
        // tail. Everything else — Chrome, Firefox, Edge — goes to hls.js.
        //
        // It is standards-track, not a WebKit guarantee: verified absent in
        // Chromium 147 bare, under --enable-blink-features=ManagedMediaSource
        // and under --enable-experimental-web-platform-features. If Chromium
        // ever ships it this test sends Chrome native again — but it fails
        // LOUDLY now rather than silently, because `onNativeError` below sees
        // readyState 0 and renders the unavailable state instead of the inert
        // play button that hid this bug for months.
        //
        // Do NOT "simplify" this to `Hls.isSupported()` first. hls.js's
        // `isSupported()` resolves through `getMediaSource()`, which PREFERS
        // ManagedMediaSource, so it returns true on modern Safari too; sending
        // Safari down the MSE path would silently kill AirPlay, because hls.js
        // sets `media.disableRemotePlayback = true` for any ManagedMediaSource
        // (hls.js@1.6.16 dist/hls.js:19312).
        const hasNativeHls =
          video.canPlayType('application/vnd.apple.mpegurl') !== '' &&
          (typeof window !== 'undefined' && 'ManagedMediaSource' in window
            ? true
            : !Hls.isSupported());

        if (!hasNativeHls && Hls.isSupported()) {
          hls = new Hls({ enableWorker: true, lowLatencyMode: false });
          hls.on(Hls.Events.ERROR, (_e, payload) => {
            if (payload.fatal) fail();
          });
          hls.loadSource(src);
          hls.attachMedia(video);
          if (autoPlay) {
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              void video.play().catch(() => {});
            });
          }
          return;
        }

        if (hasNativeHls) {
          // The element's own `error` event is the ONLY failure signal on this
          // branch — assigning an unplayable `src` throws nothing.
          //
          // Narrowed to match the hls.js branch, which fails only on
          // `payload.fatal`. A reel loops and a Mux token lives ≤15 min, so a
          // segment 403 after the token expires raises MEDIA_ERR_NETWORK
          // mid-playback; treating that as fatal would replace a *playing*
          // video with an unavailable card that has no way back. Only an error
          // that leaves the element with nothing decoded counts.
          nativeErrorTarget = video;
          video.addEventListener('error', onNativeError);
          video.src = src;
          if (autoPlay) void video.play().catch(() => {});
          return;
        }

        fail();
      } catch {
        fail();
      }
    })();

    return () => {
      cancelled = true;
      nativeErrorTarget?.removeEventListener('error', onNativeError);
      hls?.destroy();
    };
  }, [videoRef, mediaId, autoPlay]);

  return { failed };
}
